import {
  AjusteEmVigorView,
  DesfazerPrevia,
  DesfazerResultado,
  EnrichedOp,
  ImportOpsPayload,
  ImportResult,
  ISaidasClassificadas,
  LiberacaoEstado,
  ListOpsQuery,
  PaginatedOpsResult,
  SaidaClassificacao,
  UltimaImportacao,
  UpdateOpPayload,
} from '@/domain/ops/interfaces/ops';
import { opsRepository } from '@/domain/ops/repositories/opsRepository';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';
import { createStore } from '@/lib/zustand';

interface OpsState {
  result: PaginatedOpsResult | null;
  loading: boolean;
  /** The last load failed: the page shows the error instead of a fake empty list. */
  failed: boolean;
  /** The whole portfolio, for the screens that read it at once; `null` before the sweep. */
  carteira: Array<EnrichedOp> | null;
  carteiraLoading: boolean;
  carteiraFailed: boolean;
  recalculating: boolean;
  importing: boolean;
  query: ListOpsQuery;
  /** The manual adjustments IN FORCE, one row per order: own value, loading, failure. */
  ajustes: Array<AjusteEmVigorView>;
  ajustesLoading: boolean;
  ajustesFailed: boolean;
  /** Id being undone, so only that row shows progress. */
  desfazendoId: string | null;
  /** Uid coming back to the portfolio, so only that card shows progress. */
  voltandoUid: string | null;
  /** O lote de saídas está sendo classificado: a barra de seleção mostra o progresso. */
  classificandoLote: boolean;
  /** The last import receipt: its own value, own loading and own failure. */
  ultimaImportacao: UltimaImportacao | null;
  ultimaImportacaoLoading: boolean;
  ultimaImportacaoFailed: boolean;
  /** A última e as anteriores, para o cartão listar o que já entrou na carteira. */
  importacoes: Array<UltimaImportacao>;
  importacoesLoading: boolean;
  importacoesFailed: boolean;
  /** O id da importação sendo desfeita, para só aquele botão mostrar progresso. */
  desfazendoImportacaoId: string | null;
  /**
   * Loads one page and returns exactly what it fetched (the DataTable renders the
   * returned page; the summary strip reads `result`). Throws on failure so the caller
   * shows the API message.
   */
  load: (query?: ListOpsQuery) => Promise<PaginatedOpsResult>;
  /** Recomputes the engine on the server. Throws on failure; the caller reloads the table. */
  recalcular: () => Promise<void>;
  update: (uid: string, payload: UpdateOpPayload) => Promise<EnrichedOp>;
  marcarLiberacao: (uid: string, chave: string, estado: LiberacaoEstado) => Promise<EnrichedOp>;
  importar: (payload: ImportOpsPayload) => Promise<ImportResult>;
  /** O recibo da última importação gravada. Never throws: a tela lê `*Failed`. */
  loadUltimaImportacao: () => Promise<void>;
  /** A lista de importações. Never throws: a tela lê `importacoesFailed`. */
  loadImportacoes: () => Promise<void>;
  /** O que o desfazer faria. Throws para a tela mostrar a mensagem da API. */
  previaDoDesfazer: () => Promise<DesfazerPrevia>;
  /** Desfaz a última importação. Throws para a tela mostrar a mensagem da API. */
  desfazerImportacao: (id: string) => Promise<DesfazerResultado>;
  /** Undoes an exit. Throws on failure so the caller shows the API message. */
  voltarParaCarteira: (uid: string) => Promise<EnrichedOp>;
  /** Tira a OP de Excluídos. Throws para a tela mostrar a mensagem da API. */
  restaurarOp: (uid: string) => Promise<EnrichedOp>;
  /** Tira a OP da carteira com motivo. Throws para a tela mostrar a mensagem da API. */
  tirarDaCarteira: (uid: string, motivo: string, classificacao: SaidaClassificacao) => Promise<EnrichedOp>;
  /** Corrige o tipo de uma saída já gravada, sem mexer na data nem no motivo. */
  reclassificarSaida: (uid: string, classificacao: SaidaClassificacao) => Promise<EnrichedOp>;
  /** O mesmo tipo na seleção inteira. Throws para a tela mostrar a mensagem da API. */
  reclassificarSaidasEmLote: (
    uids: ReadonlyArray<string>,
    classificacao: SaidaClassificacao,
  ) => Promise<ISaidasClassificadas>;
  /** Loads the adjustments in force. Never throws: the panel reads `ajustesFailed`. */
  loadAjustes: () => Promise<void>;
  /** Reverts one adjustment. Throws on failure so the caller shows the API message. */
  desfazerAjuste: (id: string) => Promise<void>;
  /**
   * The WHOLE portfolio in memory, page after page. The Visão executiva speaks of the
   * portfolio and not of a page of it (risk by brand, by cell, punctuality), and the
   * API caps a page at 100 rows. Never throws: the screen reads `carteiraFailed`.
   */
  loadCarteira: () => Promise<void>;
}

// Only the latest load may write `result`: a slow older page must not overwrite a newer one.
let loadSequence = 0;
/** The same guard for the whole portfolio: a slower older sweep never paints over a newer one. */
let carteiraSequence = 0;
/** The API caps a page at 100 rows (`ListQueryDto.MAX_LIMIT`), so the sweep pages by 100. */
const CARTEIRA_PAGE_SIZE = 100;

export const opsStore = createStore<OpsState>((set, get) => ({
  result: null,
  loading: false,
  failed: false,
  carteira: null,
  carteiraLoading: false,
  carteiraFailed: false,
  recalculating: false,
  importing: false,
  query: { page: 1, limit: 20 },
  ajustes: [],
  ajustesLoading: false,
  ajustesFailed: false,
  desfazendoId: null,
  voltandoUid: null,
  classificandoLote: false,
  ultimaImportacao: null,
  ultimaImportacaoLoading: false,
  ultimaImportacaoFailed: false,
  importacoes: [],
  importacoesLoading: false,
  importacoesFailed: false,
  desfazendoImportacaoId: null,

  load: async (query) => {
    const resolved = query ?? get().query;
    loadSequence += 1;
    const sequence = loadSequence;
    set({ loading: true, query: resolved });
    try {
      const result = await opsRepository.list(resolved);
      if (sequence === loadSequence) {
        set({ result, failed: false });
      }
      return result;
    } catch (error) {
      logger.error('[opsStore] failed to load ops', error);
      if (sequence === loadSequence) {
        set({ failed: true });
      }
      throw error;
    } finally {
      if (sequence === loadSequence) {
        set({ loading: false });
      }
    }
  },

  recalcular: async () => {
    set({ recalculating: true });
    try {
      await opsRepository.recalcular();
    } catch (error) {
      logger.error('[opsStore] failed to recalcular', error);
      throw error;
    } finally {
      set({ recalculating: false });
    }
  },

  update: async (uid, payload) => {
    try {
      const updated = await opsRepository.update(uid, payload);
      await get().load(get().query);
      return updated;
    } catch (error) {
      logger.error('[opsStore] failed to update op', error);
      throw error;
    }
  },

  marcarLiberacao: async (uid, chave, estado) => {
    try {
      const atualizada = await opsRepository.marcarLiberacao(uid, chave, estado);
      await get().load(get().query);
      return atualizada;
    } catch (error) {
      logger.error('[opsStore] failed to mark the release checklist item', error);
      throw error;
    }
  },

  voltarParaCarteira: async (uid) => {
    set({ voltandoUid: uid });
    try {
      const atualizada = await opsRepository.voltarParaCarteira(uid);
      await get().load(get().query);
      return atualizada;
    } catch (error) {
      logger.error('[opsStore] failed to voltar OP para a carteira', error);
      throw error;
    } finally {
      set({ voltandoUid: null });
    }
  },

  // Mesmo `voltandoUid` do retorno: a ficha mostra progresso no MESMO lugar, e as duas
  // ações são a mesma decisão vista de lados opostos. A classificação atravessa até o
  // repositório sem ser interpretada: quem decide o que ela significa é o relatório.
  tirarDaCarteira: async (uid, motivo, classificacao) => {
    set({ voltandoUid: uid });
    try {
      const atualizada = await opsRepository.tirarDaCarteira(uid, motivo, classificacao);
      await get().load(get().query);
      return atualizada;
    } catch (error) {
      logger.error('[opsStore] failed to remove the OP from the portfolio', error);
      throw error;
    } finally {
      set({ voltandoUid: null });
    }
  },

  /**
   * Corrige o TIPO de uma saída já gravada. Recarrega a lista depois: a aba "Saíram"
   * mostra a classificação em cada linha, e o indicador de promessa muda com ela.
   */
  reclassificarSaida: async (uid, classificacao) => {
    set({ voltandoUid: uid });
    try {
      const atualizada = await opsRepository.reclassificarSaida(uid, classificacao);
      await get().load(get().query);
      return atualizada;
    } catch (error) {
      logger.error('[opsStore] failed to reclassify the OP exit', error);
      throw error;
    } finally {
      set({ voltandoUid: null });
    }
  },

  /**
   * O mesmo tipo na seleção inteira. Recarrega a lista uma vez no fim, como o caminho
   * de uma OP só: a aba mostra o tipo em cada linha e o indicador muda junto.
   */
  reclassificarSaidasEmLote: async (uids, classificacao) => {
    set({ classificandoLote: true });
    try {
      const resultado = await opsRepository.reclassificarSaidasEmLote(uids, classificacao);
      await get().load(get().query);
      return resultado;
    } catch (error) {
      logger.error('[opsStore] failed to reclassify the selected OP exits', error);
      throw error;
    } finally {
      set({ classificandoLote: false });
    }
  },

  // Reusa `voltandoUid`: a ficha mostra progresso no MESMO lugar, e as duas ações são
  // a mesma coisa para quem olha (a OP volta para a carteira).
  restaurarOp: async (uid) => {
    set({ voltandoUid: uid });
    try {
      const atualizada = await opsRepository.restaurar(uid);
      await get().load(get().query);
      return atualizada;
    } catch (error) {
      logger.error('[opsStore] failed to restore the OP from the trash', error);
      throw error;
    } finally {
      set({ voltandoUid: null });
    }
  },

  importar: async (payload) => {
    set({ importing: true });
    try {
      return await opsRepository.importar(payload);
    } catch (error) {
      logger.error('[opsStore] failed to importar ops', error);
      throw error;
    } finally {
      set({ importing: false });
    }
  },

  loadUltimaImportacao: async () => {
    set({ ultimaImportacaoLoading: true });
    try {
      const ultimaImportacao = await opsRepository.ultimaImportacao();
      set({ ultimaImportacao, ultimaImportacaoFailed: false });
    } catch (error) {
      logger.error('[opsStore] failed to load the last import receipt', error);
      set({ ultimaImportacaoFailed: true });
    } finally {
      set({ ultimaImportacaoLoading: false });
    }
  },

  loadImportacoes: async () => {
    set({ importacoesLoading: true });
    try {
      const importacoes = await opsRepository.importacoes();
      set({ importacoes: importacoes ?? [], importacoesFailed: false });
    } catch (error) {
      logger.error('[opsStore] failed to load the import list', error);
      set({ importacoesFailed: true });
    } finally {
      set({ importacoesLoading: false });
    }
  },

  previaDoDesfazer: async () => {
    try {
      return await opsRepository.previaDoDesfazer();
    } catch (error) {
      logger.error('[opsStore] failed to load the undo preview', error);
      throw error;
    }
  },

  // Recarrega as duas listas no fim: depois de desfazer, o cartao mostra a MESMA
  // importacao marcada como desfeita, e nao a promessa de um botao que ja foi usado.
  desfazerImportacao: async (id) => {
    set({ desfazendoImportacaoId: id });
    try {
      const resultado = await opsRepository.desfazerImportacao(id);
      await get().loadUltimaImportacao();
      await get().loadImportacoes();
      return resultado;
    } catch (error) {
      logger.error('[opsStore] failed to undo the import', error);
      throw error;
    } finally {
      set({ desfazendoImportacaoId: null });
    }
  },

  loadCarteira: async () => {
    carteiraSequence += 1;
    const sequence = carteiraSequence;
    set({ carteiraLoading: true });
    try {
      const primeira = await opsRepository.list({
        page: 1,
        limit: CARTEIRA_PAGE_SIZE,
        situacao: 'carteira',
      });
      const ops = [...(primeira.data ?? [])];
      for (let page = 2; page <= (primeira.totalPages ?? 1); page += 1) {
        const seguinte = await opsRepository.list({
          page,
          limit: CARTEIRA_PAGE_SIZE,
          situacao: 'carteira',
        });
        ops.push(...(seguinte.data ?? []));
      }
      if (sequence === carteiraSequence) {
        set({ carteira: ops, carteiraFailed: false });
      }
    } catch (error) {
      logger.error('[opsStore] failed to load carteira completa', error);
      if (sequence === carteiraSequence) {
        set({ carteiraFailed: true });
      }
    } finally {
      if (sequence === carteiraSequence) {
        set({ carteiraLoading: false });
      }
    }
  },

  loadAjustes: async () => {
    set({ ajustesLoading: true });
    try {
      const ajustes = await opsRepository.ajustesEmVigor();
      set({ ajustes: ajustes ?? [], ajustesFailed: false });
    } catch (error) {
      logger.error('[opsStore] failed to load ajustes em vigor', error);
      set({ ajustesFailed: true });
    } finally {
      set({ ajustesLoading: false });
    }
  },

  /**
   * The API refuses an undo that is no longer safe, so the list is reloaded either
   * way: on success the row shows as undone, on conflict it shows the new reason.
   */
  desfazerAjuste: async (id) => {
    set({ desfazendoId: id });
    try {
      await opsRepository.desfazerAjuste(id);
    } catch (error) {
      logger.error('[opsStore] failed to desfazer ajuste manual', error);
      throw error;
    } finally {
      set({ desfazendoId: null });
      await get().loadAjustes();
    }
  },
}));
